import React, { useEffect, useRef, useState } from 'react';
import * as am5 from '@amcharts/amcharts5';
import * as am5map from '@amcharts/amcharts5/map';
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated';
import am5geodata_worldLow from '@amcharts/amcharts5-geodata/worldLow';
import { useNavigate } from 'react-router-dom';
import { useGlobalState } from '../../contexts/GlobalStateContext';
import { useTranslation } from 'react-i18next';
import './styles.css';

const Globe = () => {
  const { t } = useTranslation();
  const chartRef = useRef(null);
  const containerRef = useRef(null);
  const [isRotating, setIsRotating] = useState(true);
  const [shouldRender, setShouldRender] = useState(false);
  const navigate = useNavigate();
  const { showLogin } = useGlobalState() || {};

  useEffect(() => {
    // Só monta o globo se a viewport for larga e a seção entrar na tela
    const isWide = () => typeof window !== 'undefined' && window.innerWidth >= 768;

    if (!isWide()) {
      setShouldRender(false);
      return undefined;
    }

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setShouldRender(true);
          observer.disconnect();
        }
      });
    }, { threshold: 0.15 });

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!shouldRender) return undefined;

    let root = am5.Root.new("chartdiv");
    let chart;
    let rotationAnimation;

    root.setThemes([am5themes_Animated.new(root)]);

    // Desabilitar logo do amCharts
    root.container.set("paddingBottom", 0);

    chart = root.container.children.push(
      am5map.MapChart.new(root, {
        panX: "rotateX",
        panY: "rotateY",
        projection: am5map.geoOrthographic(),
        paddingBottom: 20,
        paddingTop: 20,
        paddingLeft: 20,
        paddingRight: 20
      })
    );

    let polygonSeries = chart.series.push(
      am5map.MapPolygonSeries.new(root, {
        geoJSON: am5geodata_worldLow
      })
    );

    polygonSeries.mapPolygons.template.setAll({
      tooltipText: "{name}",
      toggleKey: "active",
      interactive: true,
      fill: am5.color(0xb58452),
      cursorOverStyle: "pointer"
    });

    polygonSeries.mapPolygons.template.states.create("hover", {
      fill: am5.color(0xb79473)
    });

    polygonSeries.mapPolygons.template.states.create("active", {
      fill: am5.color(0x1c4846)
    });

    let backgroundSeries = chart.series.push(am5map.MapPolygonSeries.new(root, {}));
    backgroundSeries.mapPolygons.template.setAll({
      fill: am5.color(0x000000),
      fillOpacity: 0.1,
      strokeOpacity: 0
    });

    backgroundSeries.data.push({
      geometry: am5map.getGeoRectangle(90, 180, -90, -180)
    });

    let graticuleSeries = chart.series.unshift(
      am5map.GraticuleSeries.new(root, {
        step: 10
      })
    );

    graticuleSeries.mapLines.template.setAll({
      stroke: am5.color(0xCCCCCC),
      strokeOpacity: 0.1
    });

    const startRotation = () => {
      if (rotationAnimation) {
        rotationAnimation.stop();
      }

      rotationAnimation = chart.animate({
        key: "rotationX",
        from: chart.get("rotationX"),
        to: chart.get("rotationX") + 360,
        duration: 30000,
        loops: Infinity
      });
      setIsRotating(true);
    };

    const stopRotation = () => {
      if (rotationAnimation) {
        rotationAnimation.stop();
        rotationAnimation = null;
      }
      setIsRotating(false);
    };

    const handleCountryClick = (countryName) => {
      const q = encodeURIComponent(countryName || '');
      navigate(`/search-results?country=${q}`);
    };

    polygonSeries.mapPolygons.template.events.on("click", function(ev) {
      const countryId = ev.target.dataItem.get("id");
      
      stopRotation();
      
      const dataItem = polygonSeries.getDataItemById(countryId);
      const target = dataItem.get("mapPolygon");
      
      if (target) {
        const centroid = target.geoCentroid();
        if (centroid) {
          chart.animate({
            key: "rotationX",
            to: -centroid.longitude,
            duration: 1500,
            easing: am5.ease.inOut(am5.ease.cubic)
          });
          chart.animate({
            key: "rotationY",
            to: -centroid.latitude,
            duration: 1500,
            easing: am5.ease.inOut(am5.ease.cubic)
          });

          const countryName = dataItem?.dataContext?.name || countryId;
          setTimeout(() => {
            handleCountryClick(countryName);
          }, 1500);
        }
      }
    });

    startRotation();
    chart.appear(200, 0);

    chartRef.current = {
      startRotation,
      stopRotation
    };

    return () => {
      if (rotationAnimation) {
        rotationAnimation.stop();
      }
      root.dispose();
    };
  }, [navigate, shouldRender]);

  useEffect(() => {
    if (!chartRef.current) return;
    if (showLogin) {
      chartRef.current.stopRotation?.();
    } else {
      chartRef.current.startRotation?.();
    }
  }, [showLogin]);

  const toggleRotation = () => {
    if (isRotating) {
      chartRef.current?.stopRotation();
    } else {
      chartRef.current?.startRotation();
    }
  };

  if (!shouldRender) return null;

  return (
    <div className="globe-container">
      <div className="globe-content">
        <div className="globe-text">
          <h2>{t('globalPresence.title')}</h2>
          <h3></h3>
          <h3> </h3>
          <p>
            {t('globalPresence.subtitle1')}<br />
            {t('globalPresence.subtitle2')}
          </p>
          <p></p>
          <p></p>
          <button className="search-button" onClick={() => navigate('/search-results')}>{t('globalPresence.button')}</button>
        </div>
        <div className="globe-chart-container">
          <div id="chartdiv" className="globe-chart"></div>
          <button 
            className={`rotation-control ${isRotating ? 'active' : ''}`}
            onClick={toggleRotation}
            aria-label={isRotating ? t('globe.stopRotation') : t('globe.startRotation')}
          />
        </div>
      </div>
    </div>
  );
};

export default Globe; 